// apps/admin/src/components/monitoring/ValidationHealthDashboard.tsx
import React, { useState, useEffect } from 'react';
import { ValidationService } from '../../services/ValidationService';
import type { ValidationStatus, ValidationMetrics } from '../../types/validation';
import { AlertCircle, CheckCircle, Clock, TrendingUp, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface ValidationHealthDashboardProps {
  onDataLoad?: (_statuses: ValidationStatus[]) => void;
}

export function ValidationHealthDashboard({ onDataLoad }: ValidationHealthDashboardProps) {
  const [statuses, setStatuses] = useState<ValidationStatus[]>([]);
  const [metrics, setMetrics] = useState<ValidationMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Pass data to parent when loaded
  useEffect(() => {
    if (onDataLoad && statuses.length > 0) {
      onDataLoad(statuses);
    }
  }, [statuses, onDataLoad]);

  const loadData = async () => {
    try {
      setRefreshing(true);
      const [statusData, metricsData] = await Promise.all([
        ValidationService.getValidationStatus(),
        ValidationService.getValidationMetrics()
      ]);
      setStatuses(statusData);
      setMetrics(metricsData);
    } catch (_error: any) {
  // console.error('Error loading validation data:', _error);
      toast.error('Failed to load validation data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'rebuilding':
        return <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'warning':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'error':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'rebuilding':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Never';
    try {
      // Handles both Firestore Timestamps (client-side) and serialized date objects
      if (timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleString('en-AU', {
          dateStyle: 'short',
          timeStyle: 'short'
        });
      }
      // Fallback for standard JS dates or parsable strings
      const date = new Date(timestamp);
      // Check if the fallback created a valid date
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleString('en-AU', {
        dateStyle: 'short',
        timeStyle: 'short'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading validation data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metrics Overview */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Total Companies</span>
              <TrendingUp className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-white">{metrics.totalCompanies}</p>
          </div>

          <div className="bg-green-500/10 backdrop-blur-sm rounded-xl p-4 border border-green-500/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-green-400">Healthy</span>
              <CheckCircle className="w-4 h-4 text-green-400" />
            </div>
            <p className="text-2xl font-bold text-green-400">{metrics.healthyCompanies}</p>
            <p className="text-xs text-green-400/60 mt-1">
              {metrics.totalCompanies > 0 
                ? `${((metrics.healthyCompanies / metrics.totalCompanies) * 100).toFixed(1)}%` 
                : '0%'}
            </p>
          </div>

          <div className="bg-yellow-500/10 backdrop-blur-sm rounded-xl p-4 border border-yellow-500/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-yellow-400">Warnings</span>
              <AlertCircle className="w-4 h-4 text-yellow-400" />
            </div>
            <p className="text-2xl font-bold text-yellow-400">{metrics.warningCompanies}</p>
          </div>

          <div className="bg-red-500/10 backdrop-blur-sm rounded-xl p-4 border border-red-500/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-red-400">Errors</span>
              <AlertCircle className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-400">{metrics.errorCompanies}</p>
          </div>

          <div className="bg-blue-500/10 backdrop-blur-sm rounded-xl p-4 border border-blue-500/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-400">Rebuilding</span>
              <RefreshCw className="w-4 h-4 text-blue-400" />
            </div>
            <p className="text-2xl font-bold text-blue-400">{metrics.rebuildingCompanies}</p>
          </div>
        </div>
      )}

      {/* Company Status Table */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-white">Company Validation Status</h3>
          <button
            onClick={loadData}
            disabled={refreshing}
            className="px-3 py-1.5 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700/50">
            <thead className="bg-slate-800/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                  Last Validation
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                  Months Validated
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                  Failed Months
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                  Last Rebuild
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {statuses.length > 0 ? (
                statuses.map((status) => (
                  <tr key={status.companyId} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{status.companyName}</div>
                      <div className="text-xs text-slate-400">{status.companyId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status.status)}
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(status.status)}`}>
                          {status.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {formatTimestamp(status.lastValidationRun)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {status.monthsValidated}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {status.monthsFailed > 0 ? (
                        <div>
                          <span className="text-sm font-medium text-red-400">{status.monthsFailed}</span>
                          <div className="text-xs text-slate-400 mt-1">
                            {status.failedMonths.slice(0, 2).join(', ')}
                            {status.failedMonths.length > 2 && '...'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-green-400">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {status.lastRebuildAt ? (
                        <div>
                          <div className="text-sm text-slate-300">{formatTimestamp(status.lastRebuildAt)}</div>
                          <div className="text-xs text-slate-400">
                            Duration: {formatDuration(status.lastRebuildDuration)}
                          </div>
                          {status.lastRebuildTriggeredBy && (
                            <div className="text-xs text-slate-500">By: {status.lastRebuildTriggeredBy}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-500">Never</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No validation data available</p>
                    <p className="text-sm text-slate-500 mt-2">Validation runs daily at 3:00 AM AEST</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}